import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Loading from '../../components/Common-components/loading';
import InfoButton from '../../components/Common-components/infoButton';
import { Modal, Nav } from 'react-bootstrap';
import '../../css/Booking/guestRoomSelector.css';
import { Reset } from "../../assets/icons";
import { apiGet } from '../../utils/api';
import { isDisneyAdventure, regroupDisneyAdventureCabins } from '../../utils/disneyAdventureCabinGroups';
import { ReactComponent as Arrow } from '../../assets/icons/svg/arrowUnder.svg'
import {
  buildCruisePaxDataUrl,
  buildBookingBasicInfoUrl,
  buildBookingDeckInfoUrl,
  canBuildBookingDeckInfoRequest,
  resolveTraveltekResultNo,
  getBookingGroupsCacheKey,
  isBookingBasketCacheKeyForSchedule,
} from '../../utils/traveltekBookingApi';

/** ───────────── 헬퍼들 ───────────── */
const PRIORITY = ["inside","outside","balcony","suite","인사이드","오션뷰","발코니","스위트"];
const normalize = (s='') => s.toString().trim().toLowerCase();
const DEFAULT_NO_ROOM_MESSAGE = '예약 가능한 객실이 없습니다.';
const INACTIVE_CRUISE_MESSAGE = '현재 판매가 중단되었거나 조회할 수 없는 항차입니다.';

/** 카테고리 우선순 정렬(인사이드→오션뷰→발코니→스위트 우선) */
function sortCategoriesByPriority(cats=[]) {
  return [...cats].sort((a,b)=>{
    const aName = normalize(a?.name);
    const bName = normalize(b?.name);
    const aIdx = PRIORITY.findIndex(k => aName.includes(k));
    const bIdx = PRIORITY.findIndex(k => bName.includes(k));
    const ai = aIdx === -1 ? 999 : aIdx;
    const bi = bIdx === -1 ? 999 : bIdx;
    if (ai !== bi) return ai - bi;
    return aName.localeCompare(bName);
  });
}

/** 그 등급에서 gridpricing의 "첫 번째"를 대표로 선택 */
function pickByFirstGrid(category) {
  if (!category?.gradeList?.length) return null;
  for (const room of category.gradeList) {
    const first = room.gridpricing?.[0];
    const ratecode = first?.rate?.code ?? first?.ratecode;
    const gradeno  = first?.gradeno;
    const resultno = resolveTraveltekResultNo(first?.resultno ?? room?.resultno, gradeno ?? room?.grade_no);
    if (first && canBuildBookingDeckInfoRequest({ gradeno, ratecode, resultno })) {
      return { gradeno, ratecode, resultno };
    }
  }
  return null;
}

/** 선택된 옵션에서 호출에 필요한 값 뽑기 */
function pickFromSelected(room, selectedOption) {
  const ratecode = selectedOption?.rate?.code ?? selectedOption?.ratecode;
  const gradeno  = selectedOption?.gradeno;
  const resultno = resolveTraveltekResultNo(selectedOption?.resultno ?? room?.resultno, gradeno ?? room?.grade_no);
  if (canBuildBookingDeckInfoRequest({ gradeno, ratecode, resultno })) {
    return { gradeno, ratecode, resultno };
  }
  return null;
}

// 층수 뽑기
function formatDeckLabel(deck) {
  if (!deck) return '';
  const name = deck.name ?? '';
  if (typeof name === 'string') {
    const match = name.match(/(\d+)/);
    if (match) {
      const parsed = Number.parseInt(match[1], 10);
      if (Number.isFinite(parsed)) {
        return `${parsed}F`;
      }
      return `${match[1]}F`;
    }
    return name;
  }
  return '';
}

const CABIN_META_LABELS = {
  size: ['크기', 'size'],
  capacity: ['인원', '정원', 'occupancy', 'capacity', 'guests'],
  bed: ['침대', 'beds', 'bed'],
  bath: ['욕실', 'bath', 'bathroom', 'bathrooms'],
  feature: ['선실특징', '특징', 'feature', 'features'],
  deck: ['층', 'deck', 'decks'],
};

const normalizeCabinText = (text = '') => (
  text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\r\\n|\\r|\\n/g, '\n')
    .replace(/\r\n|\r|\n/g, '\n')
);

const stripLine = (line = '') => line.replace(/^[-•]\s*/, '').trim();

const pickCabinMetaKey = (label = '') => {
  const lowered = label.toLowerCase();
  for (const [key, candidates] of Object.entries(CABIN_META_LABELS)) {
    if (candidates.some(candidate => lowered === candidate || lowered.includes(candidate))) {
      return key;
    }
  }
  return null;
};

const parseCabinSize = (value = '') => {
  const result = {};
  const cabinMatch = value.match(/(?:선실|stateroom)\s*[:：]?\s*([0-9]+(?:\.[0-9]+)?\s*(?:㎡|m2|sq\.?\s*ft\.?))/i);
  if (cabinMatch) {
    result.cabinSize = cabinMatch[1].replace(/\s+/g, ' ').trim();
  }
  const balconyMatch = value.match(/(?:발코니|balcony)\s*[:：]?\s*([0-9]+(?:\.[0-9]+)?\s*(?:㎡|m2|sq\.?\s*ft\.?))/i);
  if (balconyMatch) {
    result.balconySize = balconyMatch[1].replace(/\s+/g, ' ').trim();
  }
  if (!result.cabinSize) {
    const genericMatch = value.match(/([0-9]+(?:\.[0-9]+)?\s*(?:㎡|m2|sq\.?\s*ft\.?))/i);
    if (genericMatch) {
      result.cabinSize = genericMatch[1].replace(/\s+/g, ' ').trim();
    }
  }
  return result;
};

const parseCabinCapacity = (value = '') => {
  const normalized = value.replace(/\s+/g, ' ').trim();
  const koMatch = normalized.match(/최대\s*([0-9]+)\s*인/);
  if (koMatch) return `${koMatch[1]}인`;
  const enMatch = normalized.match(/sleeps\s*up to\s*([0-9]+)/i);
  if (enMatch) return `${enMatch[1]}인`;
  const countMatch = normalized.match(/([0-9]+)\s*(?:인|명|guests|persons|people)/i);
  if (countMatch) return `${countMatch[1]}인`;
  return normalized;
};

const parseCabinMeta = (description = '') => {
  if (!description) return null;
  const lines = normalizeCabinText(description)
    .split('\n')
    .map(stripLine)
    .filter(Boolean);
  if (lines.length === 0) return null;

  const meta = {
    maxGuest: null,
    cabinSize: null,
    balconySize: null,
    bed: null,
    bath: null,
    feature: null,
    deck: null,
  };
  let pendingKey = null;

  const applyValue = (key, value) => {
    if (!value) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (key === 'size') {
      const sizeParts = parseCabinSize(trimmed);
      if (!meta.cabinSize && sizeParts.cabinSize) meta.cabinSize = sizeParts.cabinSize;
      if (!meta.balconySize && sizeParts.balconySize) meta.balconySize = sizeParts.balconySize;
      if (!meta.cabinSize && !meta.balconySize) meta.cabinSize = trimmed;
      return;
    }
    if (key === 'capacity') {
      if (!meta.maxGuest) meta.maxGuest = parseCabinCapacity(trimmed);
      return;
    }
    if (key === 'bed' && !meta.bed) meta.bed = trimmed;
    if (key === 'bath' && !meta.bath) meta.bath = trimmed;
    if (key === 'feature' && !meta.feature) meta.feature = trimmed;
    if (key === 'deck' && !meta.deck) meta.deck = trimmed;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (pendingKey) {
      applyValue(pendingKey, line);
      pendingKey = null;
      continue;
    }
    const colonMatch = line.match(/^([^:：]+)\s*[:：]\s*(.+)$/);
    if (colonMatch) {
      const label = colonMatch[1].trim();
      const value = colonMatch[2].trim();
      const key = pickCabinMetaKey(label);
      if (key) {
        applyValue(key, value);
        continue;
      }
    }

    const directKey = pickCabinMetaKey(line);
    if (directKey) {
      const normalizedLine = line.toLowerCase();
      const isLabelOnly = CABIN_META_LABELS[directKey].some(candidate => normalizedLine === candidate);
      if (isLabelOnly) {
        pendingKey = directKey;
        continue;
      }
    }

    if (!meta.maxGuest) {
      const guessCapacity = parseCabinCapacity(line);
      if (guessCapacity && /[0-9]/.test(guessCapacity)) {
        meta.maxGuest = guessCapacity;
      }
    }
  }

  const cleaned = Object.fromEntries(
    Object.entries(meta).filter(([, value]) => value)
  );
  return Object.keys(cleaned).length > 0 ? cleaned : null;
};

/** 나이 배열을 YYYY-MM-DD 형태 DOB 목록으로 변환 (출발일 기준) */
function buildDobList({ cruiseDepartureDate, count, ages }) {
  const totalNeeded = Number(count || 0);
  if (totalNeeded <= 0) return '';

  const fallbackDob = '0000-01-01';
  const rawDeparture = cruiseDepartureDate ? new Date(cruiseDepartureDate) : null;
  const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());
  const baseDate = isValidDate(rawDeparture) ? rawDeparture : new Date();

  const baseYear = baseDate.getFullYear();
  const baseMonth = baseDate.getMonth(); // 0-based
  const baseDay = baseDate.getDate();
  const pad = (value) => String(value).padStart(2, '0');

  const mapAgeToDob = (ageStr) => {
    const ageNum = Number(ageStr);
    if (!Number.isFinite(ageNum) || ageNum < 0) return fallbackDob;

    const birthYear = Math.max(1900, baseYear - Math.floor(ageNum));
    let dob = new Date(birthYear, baseMonth, baseDay);

    if (!isValidDate(dob) || dob.getMonth() !== baseMonth) {
      dob = new Date(birthYear, baseMonth + 1, 0);
      if (!isValidDate(dob)) return fallbackDob;
    }

    const yyyy = dob.getFullYear().toString().padStart(4, '0');
    const mm = pad(dob.getMonth() + 1);
    const dd = pad(dob.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  const segments = [];
  if (Array.isArray(ages)) {
    ages.slice(0, totalNeeded).forEach(age => {
      segments.push(mapAgeToDob(age));
    });
  }

  while (segments.length < totalNeeded) {
    segments.push(fallbackDob);
  }

  return segments.join(',');
}

/** 소아 DOB */
function buildChildDob({ cruiseDepartureDate, childCount, childAges }) {
  return buildDobList({ cruiseDepartureDate, count: childCount, ages: childAges });
}

/** 시니어 체크 시 성인 DOB (기본 만 70세 기준) */
function buildSeniorDob({ cruiseDepartureDate, adultCount, referenceAge = 70 }) {
  const count = Number(adultCount) || 0;
  if (count <= 0) return '';
  const ages = Array.from({ length: count }, () => referenceAge);
  return buildDobList({ cruiseDepartureDate, count, ages });
}

function normalizeGuestCounts(counts) {
  if (!counts || typeof counts !== 'object') {
    return { adults: 0, children: 0 };
  }
  const adults = Number(counts.adults) || 0;
  const children = Number(counts.children) || 0;
  return { adults, children };
}

function normalizeGuestAges(ages) {
  if (!ages || typeof ages !== 'object') {
    return { children: [] };
  }
  const childAges = Array.isArray(ages.children) ? [...ages.children] : [];
  return { children: childAges };
}

function normalizePastPax(value) {
  return String(value || '').trim();
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  return '';
}

function normalizeMembershipGender(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (['m', 'male', 'mr', 'sir'].includes(normalized)) return 'male';
  if (['f', 'female', 'ms', 'mrs', 'miss', 'madam'].includes(normalized)) return 'female';
  return '';
}

function normalizeMembershipPassengerInfo(value) {
  if (!value || typeof value !== 'object') return null;

  const firstName = firstNonEmpty(value.firstName, value.firstname, value.first_name, value.forename, value.givenname);
  const lastName = firstNonEmpty(value.lastName, value.lastname, value.last_name, value.surname, value.familyname);
  const birthDate = firstNonEmpty(value.birthDate, value.dob, value.dateofbirth, value.date_of_birth);
  const gender = normalizeMembershipGender(firstNonEmpty(value.gender, value.sex, value.title));

  if (!firstName && !lastName && !birthDate && !gender) return null;
  return { firstName, lastName, birthDate, gender };
}

const DEFAULT_AGE_CONFIG = {
  adult: { minAge: 18, label: '성인(만 18세 이상)', summaryLabel: '성인' },
  child: { minAge: 1, maxAge: 17, label: '소아(만 1-17세)', summaryLabel: '소아' },
};
// 회원번호 적용 기능은 지정 선사에 한해 노출한다.
const MEMBERSHIP_ENABLED_LINE_IDS = [22];
const MEMBERSHIP_VISIBLE_DISABLED_LINE_IDS = [];
const MEMBERSHIP_LOOKUP_ENABLED_LINE_IDS = [22];
const SENIOR_ENABLED_LINE_IDS = [17, 22];
const MSC_LINE_ID = 16;
const MSC_VOYAGERS_CLUB_NOTICE = 'MSC 보이저 클럽 회원 할인 적용 가능 여부는 채팅상담을 통해 문의해 주시기 바랍니다.';
const MEMBERSHIP_PLACEHOLDER_BY_LINE_ID = {
  17: '노르웨이지안 회원번호 입력',
  22: '로얄캐리비안 회원번호 입력',
  73: '디즈니 회원번호 입력',
};

/** ───────────── 단일 게스트 카운터 Row ───────────── */
function GuestRow({ label, count, setCount, showNav }) {
  const inc = () => setCount(c => c + 1);
  const dec = () => setCount(c => Math.max(0, c - 1));
  const match = label.match(/^(.+?)\((.+)\)$/);
  const mainLabel = match ? match[1] : label;
  const ageLabel  = match ? `(${match[2]})` : '';

  return (
    <div className="guest-row">
      <div className="guest-label">
        <span className="guest-label-main">{mainLabel}</span>
        {ageLabel && <span className="guest-label-age">{ageLabel}</span>}
      </div>
      <div className="guest-count-wrapper">
        <button onClick={dec} disabled={count === 0 || showNav}>–</button>
        <span className="guest-count">{count}</span>
        <button onClick={inc} disabled={showNav}>＋</button>
      </div>
    </div>
  );
}

/**
 * 선사별 프로모션 노출 규칙 정의
 * - lineIds: 선사 식별에 사용할 Traveltek line_id 배열
 * - exclude: 리스트에서 노출하지 않을 라벨(정확히 일치)
 * - buckets: 순서대로 처리할 규칙 목록
 *   - mode: 'ordered'는 values 순서대로 1건씩, 'lowest'는 조건을 만족하는 항목 중 최저가 1건을 선택
 *   - match: 우선적으로 검사할 문자열 집합
 *       · includes: 문자열에 해당 토큰이 포함돼 있는지 검사
 *       · equals: 문자열이 토큰과 완전히 일치하는지 검사
 *       · startsWith: 토큰으로 시작하는지 검사
 *       · endsWith: 토큰으로 끝나는지 검사
*   - fallback: match에서 후보가 없을 때 대신 검사할 문자열 집합(예: includes 'nrd')
 */
const CRUISE_EXCEPTION_RULES = {
  ROYAL_CARIBBEAN: {
    lineIds: [22],
    exclude: [],
    buckets: [
      {
        mode: 'lowest',
        match: { includes: ['best price'] },
        fallback: { includes: ['nrd'] },
      },
      {
        mode: 'lowest',
        match: { includes: ['bogo60', 'savings', 'ref dep chg'] },
      },
    ],
  },
  NORWEGIAN: {
    lineIds: [17],
    exclude: [
      'mas_internet, shorex credit',
      'early booking fare',
      'ncl reduce rate percentage off',
      'prepaid service charges',
    ],
    buckets: [
      {
        mode: 'ordered',
        values: [
          { code: 'BESTFARE', equals: 'best_fare' },
          { code: 'DISXIN', includes: ['fas_dining package, internet', 'mas_dining, internet, shorex credit'] },
          { code: 'BVSXIN', includes: ['fas_bar package, internet', 'mas_beverage, internet, shorex credit'] },
          { code: 'ALL4CHO', includes: ['fas_ bar dining package, internet', 'mas_beverage, dining, internet, shorex'] },
        ],
      },
    ],
  },
};

const normalizeException = (s = '') => s.toString().trim().toLowerCase();

const getRateCode = (option) => option?.ratecode ?? option?.rate?.code ?? null;

const isSeniorPricingOption = (option) => {
  const seniorValue = option?.rate?.senior ?? option?.rate?.seniorFlag ?? option?.senior ?? option?.seniorFlag;
  return Number(seniorValue) === 1;
};

const preferSeniorPricingOptions = (options, seniorActive) => {
  if (!seniorActive || !Array.isArray(options)) return options;
  const seniorOptions = options.filter(isSeniorPricingOption);
  return seniorOptions.length > 0 ? seniorOptions : options;
};

// 문자열 금액을 안전하게 숫자로 변환 (실패 시 Infinity 처리)
const parsePrice = (value) => {
  if (value === null || value === undefined) return Infinity;
  const numeric = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(numeric) ? Infinity : numeric;
};

const getSeniorAwareCategoryMinPrice = (category, seniorActive) => {
  const prices = [];
  (category?.gradeList || []).forEach(room => {
    let options = (room?.gridpricing || [])
      .filter(p => p.available === 'Y')
      .sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
    options = preferSeniorPricingOptions(options, seniorActive);
    options.forEach(option => {
      const price = parsePrice(option?.price);
      if (Number.isFinite(price)) prices.push(price);
    });
  });
  return prices.length > 0 ? Math.min(...prices) : null;
};

// 배열에서 최저가 1건을 고르는 헬퍼(동일 가격이면 원래 순서 우선)
const pickLowestByPrice = (items = []) => {
  if (!Array.isArray(items) || !items.length) return null;
  return items.reduce((best, current) => {
    if (!best) return current;
    const bestPrice = parsePrice(best.price);
    const currentPrice = parsePrice(current.price);
    if (currentPrice < bestPrice) return current;
    if (currentPrice === bestPrice && (current.index ?? 0) < (best.index ?? 0)) return current;
    return best;
  }, null);
};

const identifyCruiseException = (lineId) => {
  const numericId = Number(lineId);
  if (!Number.isFinite(numericId)) return null;
  for (const [ruleKey, { lineIds = [] }] of Object.entries(CRUISE_EXCEPTION_RULES)) {
    if (lineIds.map(Number).includes(numericId)) {
      return ruleKey;
    }
  }
  return null;
};

const includesAny = (label, list = []) => list.some(word => label.includes(word));

const shouldHideRoyalRestrictedRate = (option) => {
  const normalizedName = normalizeException(option?.rate?.name ?? option?.name ?? '');
  const normalizedDescription = normalizeException(option?.rate?.description ?? option?.description ?? '');
  const nonRefundableDeposit = Number(option?.rate?.nonrefundabledeposit ?? option?.nonrefundabledeposit ?? 0);

  if (nonRefundableDeposit !== 0) return false;
  if (!['best rate', 'best value'].includes(normalizedName)) return false;

  return normalizedDescription.includes(normalizeException('Restrictions may apply'));
};

// 다양한 비교 조건(equals/includes/startsWith/endsWith)을 지원해 라벨 매칭을 유연하게 처리
const matchesRuleValue = (item, value) => {
  const label = typeof item === 'string' ? item : (item?.label || '');
  const code = normalizeException(typeof item === 'string' ? '' : (item?.code || ''));
  const normalizeTokens = (tokens) => {
    if (!tokens) return [];
    if (Array.isArray(tokens)) return tokens.map(normalizeException).filter(Boolean);
    return [normalizeException(tokens)];
  };

  if (typeof value === 'string') {
    return label === normalizeException(value);
  }

  if (!value || typeof value !== 'object') {
    return false;
  }

  const codeTokens = normalizeTokens(value.code);
  if (codeTokens.some(token => code === token)) {
    return true;
  }

  const equalsTokens = normalizeTokens(value.equals);
  if (equalsTokens.some(token => label === token)) {
    return true;
  }

  const includesTokens = normalizeTokens(value.includes);
  if (includesTokens.some(token => label.includes(token))) {
    return true;
  }

  const startsWithTokens = normalizeTokens(value.startsWith);
  if (startsWithTokens.some(token => label.startsWith(token))) {
    return true;
  }

  const endsWithTokens = normalizeTokens(value.endsWith);
  if (endsWithTokens.some(token => label.endsWith(token))) {
    return true;
  }

  return false;
};

// 선사별 프로모션 배열을 재정렬
// ROYAL_CARIBBEAN: 그룹1(최저가 Best Price → 없으면 NRD) → 그룹2(BOGO60/Savings/Ref Dep Chg) → 나머지 가격순
// NCL: 제외 목록 제거 후 전체 가격 오름차순
const applyExceptionRule = (options = [], type = null) => {
  if (!Array.isArray(options) || !type) return options;
  const rule = CRUISE_EXCEPTION_RULES[type];
  if (!rule) return options;

  const items = options.map((opt, index) => ({
    option: opt,
    label: normalizeException(opt?.rate?.name ?? opt?.name ?? ''),
    index,
    code: getRateCode(opt),
    price: parsePrice(opt?.price),
  }));

  const excludeSet = new Set((rule.exclude || []).map(normalizeException));
  // 제외 대상이 아닌 옵션만 정렬 대상으로 사용
  const available = items.filter(item => {
    if (excludeSet.has(item.label)) return false;
    if (type === 'ROYAL_CARIBBEAN' && shouldHideRoyalRestrictedRate(item.option)) return false;
    return true;
  });
  const keyFor = (item) => item.code ?? `idx-${item.index}`;

  const sortByPrice = (list) =>
    [...list].sort((a, b) => {
      const priceDiff = a.price - b.price;
      if (priceDiff !== 0) return priceDiff;
      return a.index - b.index;
    });

  let prioritizedItems = [];

  if (type === 'ROYAL_CARIBBEAN') {
    const group1Bucket = rule.buckets?.[0] || {};
    const group2Bucket = rule.buckets?.[1] || {};

    const group1Includes = (group1Bucket.match?.includes || []).map(normalizeException);
    const group1FallbackIncludes = (group1Bucket.fallback?.includes || []).map(normalizeException);
    const group2Includes = (group2Bucket.match?.includes || []).map(normalizeException);

    let remaining = [...available];

    const takeLowest = (predicate) => {
      const matches = remaining.filter(predicate);
      const picked = pickLowestByPrice(matches);
      if (!picked) return null;
      const key = keyFor(picked);
      const removeIndex = remaining.findIndex(item => keyFor(item) === key);
      if (removeIndex >= 0) remaining.splice(removeIndex, 1);
      return picked;
    };

    const matchGroup1 = (item) =>
      includesAny(item.label, group1Includes) ||
      includesAny(item.label, group1FallbackIncludes);

    // 그룹1: Best Price와 NRD 중 가격이 가장 낮은 1건만 노출
    const group1Pick = takeLowest(matchGroup1);

    if (group1Pick) {
      prioritizedItems.push(group1Pick);
    }

    // 그룹1에 속하는 나머지 후보들은 숨김
    remaining = remaining.filter(item => !matchGroup1(item));

    // 그룹2: BOGO60/Savings/Ref Dep Chg 중 최저가 1건만 추가
    const group2Pick = takeLowest(item => includesAny(item.label, group2Includes));
    if (group2Pick) {
      prioritizedItems.push(group2Pick);
    }

    // 그룹2 후보 중 선택되지 않은 나머지도 tail에서 제외
    remaining = remaining.filter(item => !includesAny(item.label, group2Includes));

    // 나머지는 모두 가격순으로 이어 붙인다
    prioritizedItems = [
      ...prioritizedItems,
      ...sortByPrice(remaining),
    ];
  } else if (type === 'NORWEGIAN') {
    // NCL: 필터링된 전체를 가격 오름차순으로 정렬
    // prioritizedItems = sortByPrice(available); ---> 251015 와우크루즈 대표님 피드백 적용
    // NCL: 지정된 values 순서만 노출, 나머지 프로모션은 숨김 + 'mas_beverage, dining, internet, shorex'를 포함한 문자열 찾기
    const orderedValues = rule.buckets?.[0]?.values || [];
    prioritizedItems = orderedValues
      .map(value => available.find(item => matchesRuleValue(item, value)))
      .filter(Boolean);
  } else {
    // ROYAL_CARIBBEAN/NCL 이외 항차
    prioritizedItems = sortByPrice(available);
  }

  const fallbackItems = available.length > 0 ? available : items;
  const outputItems = prioritizedItems.length > 0 ? prioritizedItems : fallbackItems;
  const output = outputItems.map(item => item.option);

  return output;
};

const getRateDisplayName = (option) => {
  if (!option) return '';
  return String(option?.rate?.name_kr || option?.rate?.name || option?.name || '').trim();
};

const getRateOptionKey = (option, fallbackIndex = null) => {
  const rateCode = getRateCode(option);
  if (rateCode) return String(rateCode);

  const gradeNo = String(option?.gradeno ?? '').trim();
  if (gradeNo) return `${gradeNo}:${option?.price ?? ''}`;

  return fallbackIndex === null ? '' : `idx-${fallbackIndex}`;
};

const isNonRefundableDepositRate = (option) => {
  const rawFlag = option?.rate?.nonrefundabledeposit ?? option?.nonrefundabledeposit ?? null;
  return Number(rawFlag) === 1;
};

const buildRateDisplayNameMap = (options = []) => {
  const keyedOptions = options.map((option, index) => ({
    option,
    index,
    key: getRateOptionKey(option, index),
    baseName: getRateDisplayName(option),
    description: String(option?.rate?.description || option?.description || '').trim(),
    rateCode: String(getRateCode(option) || '').trim(),
    isNonRefundableDeposit: isNonRefundableDepositRate(option),
  }));

  const duplicateNameCounts = keyedOptions.reduce((acc, item) => {
    const normalizedName = normalize(item.baseName);
    if (!normalizedName) return acc;
    acc.set(normalizedName, (acc.get(normalizedName) || 0) + 1);
    return acc;
  }, new Map());

  const resolvedLabelCounts = new Map();
  keyedOptions.forEach((item) => {
    const baseLabel = item.baseName;
    if (!baseLabel) return;

    const hasDuplicateName = (duplicateNameCounts.get(normalize(baseLabel)) || 0) > 1;
    const resolvedLabel = hasDuplicateName && item.isNonRefundableDeposit
      ? `${baseLabel} - 예약금 환불불가`
      : baseLabel;

    item.resolvedLabel = resolvedLabel;
    const normalizedResolvedLabel = normalize(resolvedLabel);
    if (!normalizedResolvedLabel) return;
    resolvedLabelCounts.set(normalizedResolvedLabel, (resolvedLabelCounts.get(normalizedResolvedLabel) || 0) + 1);
  });

  return keyedOptions.reduce((acc, item) => {
    let label = item.resolvedLabel || item.baseName;
    const normalizedLabel = normalize(label);
    const hasDuplicateResolvedLabel = normalizedLabel && (resolvedLabelCounts.get(normalizedLabel) || 0) > 1;

    if (hasDuplicateResolvedLabel) {
      const fallbackLabel = item.description || item.rateCode;
      if (fallbackLabel) {
        label = `${label} (${fallbackLabel})`;
      }
    }

    acc.set(item.key, label);
    return acc;
  }, new Map());
};

/** ───────────── 등급(카테고리) 카드 리스트 ─────────────
 *  - 선택하기 클릭 시, 선택 옵션 기준으로 deck_info를 호출해서 onChoose로 전달
 */

// 250830 드롭 다운 추가
function RateDropdown({ availablePricingOptions, selectedOption, onSelectOption, hasGuaranteed, isSeniorFlag }) {
  const [open, setOpen] = React.useState(false);
  const [animatePrice, setAnimatePrice] = React.useState(false);
  const labelMap = useMemo(() => buildRateDisplayNameMap(availablePricingOptions), [availablePricingOptions]);
  const selectedRateCode = getRateCode(selectedOption);

  const getOptionLabel = React.useCallback((option, index = null) => {
    const labelKey = getRateOptionKey(option, index);
    return labelMap.get(labelKey) || getRateDisplayName(option) || '옵션 선택';
  }, [labelMap]);

  React.useEffect(() => {
    setAnimatePrice(false);
    const id = setTimeout(() => setAnimatePrice(true), 10);
    return () => clearTimeout(id);
  }, [selectedOption?.price]);

  return (
    <div className="room-card-dropdown">
      <div className={`dropdown-display ${open ? 'is-open' : ''}`} onClick={() => setOpen(p => !p)}>
        <span className="dropdown-display__label">{getOptionLabel(selectedOption) || "옵션 선택"}</span>
        <span className={`dropdown-display__caret ${open ? 'is-open' : ''}`}><Arrow /></span>
      </div>

      {open && (
        <ul className="dropdown-options">
          {availablePricingOptions.map((opt, index) => (
            <li
              key={opt.ratecode ?? opt.rate?.code}
              onClick={() => {
                onSelectOption?.(opt);
                setOpen(false);
              }}
              className={`dropdown-options__item ${selectedRateCode === (opt.ratecode ?? opt.rate?.code) ? 'is-selected' : ''}`}
            >
              {getOptionLabel(opt, index)}
            </li>
          ))}
        </ul>
      )}

      {/* 여기에 개런티 적용하기 */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems: 'flex-start'}}>
        <p className={`room-price ${animatePrice ? 'price-wave-effect' : ''}`}>
          {Number(selectedOption?.price || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} {isSeniorFlag ? <span className='isSeniorPrice'>(시니어 할인)</span> : ''}
        </p>

        {hasGuaranteed && (
          <div style={{display:'flex',alignItems:'center', gap:'6px'}}>
            개런티
            <InfoButton placement="top" offsetY={8}>
              <div style={{maxWidth: 'auto'}}>
                <p style={{margin: 0, fontWeight: 600}}>개런티 객실 안내</p>
                <p style={{margin: '8px 0 0', fontSize: 12, lineHeight: 1.6}}>해당 등급은 개런티(선실 임의 배정) 옵션이 포함될 수 있습니다.<br/>최종 선실 번호는 출항 전 확정됩니다.</p>
              </div>
            </InfoButton>
          </div>
        )}
      </div>
    </div>
  );
}

function TabContent({ category, onChoose, onRatePick, sessionKey, cruiseId, pax, exceptionType, seniorFlag, seniorActive }) {
  const [selectedRates, setSelectedRates] = useState({});
  const [isChoosing, setIsChoosing] = useState(false);
  const [pendingRateConfirmation, setPendingRateConfirmation] = useState(null);
  const categoryName = category?.name || '';

  const totalChildren = Number(pax?.children || 0);

  const applyRateChange = React.useCallback((cabinCode, option) => {
    if (!cabinCode || !option) return;

    const nextRateCode = getRateCode(option);
    if (!nextRateCode) return;

    setSelectedRates(prev => ({ ...prev, [cabinCode]: nextRateCode }));
    onRatePick?.(categoryName, Number(option?.price || 0));
  }, [categoryName, onRatePick]);

  const closeRateConfirmation = React.useCallback(() => {
    setPendingRateConfirmation(null);
  }, []);

  const continueChoose = React.useCallback(async (room, selectedOption) => {
    if (isChoosing || !room || !selectedOption) return;

    const picked = pickFromSelected(room, selectedOption);
    if (!picked) return;

    setIsChoosing(true);
    const url = buildBookingDeckInfoUrl({
      sessionKey,
      cruiseId,
      gradeno: picked.gradeno,
      resultno: picked.resultno,
      adults: pax.adults,
      childrenTotal: totalChildren,
      ratecode: picked.ratecode,
    });
    const cabinMeta = parseCabinMeta(room?.description_ko || room?.description || '');

    try {
      const fresh = await apiGet(url);
      const freshDeck = fresh?.results;
      onChoose({
        ...room,
        gradeno: picked.gradeno,
        resultno: picked.resultno,
        gridPrice: selectedOption,
        gridMeta: picked,
        deckInfo: freshDeck,
        deckImageUrl: room.image_url,
        ...(cabinMeta ? { cabinMeta } : {}),
      });
    } catch (e) {
      console.error('deck_info (onChoose) failed:', e);
    } finally {
      setIsChoosing(false);
    }
  }, [cruiseId, isChoosing, onChoose, pax.adults, sessionKey, totalChildren]);

  const handleRateConfirmationAccept = React.useCallback(async () => {
    const pendingRoom = pendingRateConfirmation?.room;
    const pendingOption = pendingRateConfirmation?.selectedOption;

    closeRateConfirmation();

    if (!pendingRoom || !pendingOption) return;
    await continueChoose(pendingRoom, pendingOption);
  }, [closeRateConfirmation, continueChoose, pendingRateConfirmation]);

  const handleRateConfirmationDecline = React.useCallback(() => {
    closeRateConfirmation();
  }, [closeRateConfirmation]);

  const handleRateSelectionRequest = React.useCallback((cabinCode, option, currentOption) => {
    const nextRateCode = getRateCode(option);
    const currentRateCode = getRateCode(currentOption);

    if (!nextRateCode || nextRateCode === currentRateCode) {
      return;
    }

    applyRateChange(cabinCode, option);
  }, [applyRateChange]);

  if (!category) return null;

  return (
    <>
      <div className="room-cards">
        {(category.gradeList || [])
          .filter(room => room.gridpricing?.some(p => p.available === 'Y'))
          .map(room => {
            let availablePricingOptions = (room.gridpricing || [])
              .filter(p => p.available === 'Y')
              .sort((a, b) => parsePrice(a.price) - parsePrice(b.price));

            // MSC/NCL 등 예외 선사에 맞춰 프로모션 옵션 재정렬
            availablePricingOptions = applyExceptionRule(availablePricingOptions, exceptionType);
            availablePricingOptions = preferSeniorPricingOptions(availablePricingOptions, seniorActive);

            if (availablePricingOptions.length === 0) return null;

            const selectedRateCode =
              selectedRates[room.cabincode] ||
              availablePricingOptions[0]?.ratecode ||
              availablePricingOptions[0]?.rate?.code;

            const selectedOption =
              availablePricingOptions.find(p => (p.ratecode ?? p.rate?.code) === selectedRateCode)
              || availablePricingOptions[0];

            // 층수
            const deckItems = Array.isArray(room.deckList) ? room.deckList : [];

            const handleChoose = () => {
              if (isChoosing) return;

              if (isNonRefundableDepositRate(selectedOption)) {
                setPendingRateConfirmation({
                  room,
                  selectedOption,
                });
                return;
              }

              continueChoose(room, selectedOption);
            };

            const baseGradeName = room?.grade_name ?? '';
            const displayGradeName = room?.grade_name_ko || baseGradeName;
            const hasGuaranteed = /개런티|guaranteed|guarantee/i.test(displayGradeName || baseGradeName);

            return (
              <div key={room.cabincode} className="room-card">
                <img src={room.image_url} alt={displayGradeName || baseGradeName} className="room-image" />
                <div className="room-contents">
                  <div className="room-info">
                    <div className='room-header-wrapper'>
                      <h2 className="room-name">
                        {displayGradeName || baseGradeName} <span className='cabin-code'>({room.cabincode})</span>
                      </h2>
                      <InfoButton placement="top" offsetY={8}>
                        <div style={{maxWidth: 'auto'}}>
                          <p style={{margin: 0, fontWeight: 600}}>객실 안내</p>
                          <ol style={{margin: '8px 0 0', fontSize: 12, lineHeight: 1.6}}>
                            {(room.description_ko || room.description)
                              ?.split(/<br\s*\/?>|\r?\n/gi)
                              .filter(line => line.trim() !== '')
                              .map((line, i) => (
                                <li key={i}>{line.trim()}</li>
                              ))}
                          </ol>
                        </div>
                      </InfoButton>
                    </div>
                    <div className="room-rate-selector-wrapper" style={{display: 'flex', flexDirection: 'column' }}>
                      <div className="room-rate-selector">
                        <RateDropdown
                          isSeniorFlag={seniorFlag}
                          availablePricingOptions={availablePricingOptions}
                          selectedOption={selectedOption}
                          onSelectOption={(option) => handleRateSelectionRequest(
                            room.cabincode,
                            option,
                            selectedOption,
                          )}
                          hasGuaranteed={hasGuaranteed}
                        />
                      </div>

                      <div className="room-floor-list">
                        {deckItems
                          .slice()
                          .reverse()
                          .map(deck => {
                            const label = formatDeckLabel(deck);
                            if (!label) return null;
                            return (
                              <span
                                key={deck.deck_id || deck.deck_row_id || `${room.cabincode}-${label}`}
                                className="room-floor"
                              >
                                {label}
                              </span>
                            );
                          })}
                      </div>
                    </div>
                </div>

                <button
                    type="button"
                    className="room-choose-btn"
                    onClick={handleChoose}
                  >
                    선택하기
                  </button>
                </div>
                {isChoosing && <Loading isLoading={true} />}
              </div>
            );
          })}
      </div>

      <Modal
        show={!!pendingRateConfirmation}
        onHide={handleRateConfirmationDecline}
        centered
        className="guest-membership-modal guest-promo-confirm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>예약금 환불불가 확인</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="guest-membership-modal-copy">
            선택하신 요금은 예약금 환불불가 조건입니다. 동의하시면 다음 단계로 진행됩니다.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="guest-membership-footer-button is-secondary"
            onClick={handleRateConfirmationDecline}
          >
            아니오
          </button>
          <button
            type="button"
            className="guest-membership-footer-button is-primary"
            onClick={handleRateConfirmationAccept}
          >
            예
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

/** ───────────── 메인: GuestRoomSelector ───────────── */
export default function GuestRoomSelector({
  cruise,
  stage = 0,
  onSubmitPax,
  onSyncPax,
  onResetPax,
  onNextStep,
  savedCounts,
  savedAges,
  savedIncludeSenior,
  savedPastPax,
  savedMembershipPassengerInfo,
  scheduleId,
  ageConfig,
}) {
  // 1) 인원수 상태
  const passengerAgeConfig = ageConfig ?? DEFAULT_AGE_CONFIG;
  const childMinAge = passengerAgeConfig?.child?.minAge ?? DEFAULT_AGE_CONFIG.child.minAge;
  const childMaxAge = passengerAgeConfig?.child?.maxAge ?? DEFAULT_AGE_CONFIG.child.maxAge;
  const seniorRule = passengerAgeConfig?.senior || null;

  const initialCounts = normalizeGuestCounts(savedCounts);
  const initialAges = normalizeGuestAges(savedAges);
  const initialPastPax = normalizePastPax(savedPastPax);

  const [adults,   setAdults]   = useState(initialCounts.adults);
  const [children, setChildren] = useState(initialCounts.children);

  const [childrenAges, setChildrenAges] = useState(initialAges.children);
  const [includeSenior, setIncludeSenior] = useState(!!savedIncludeSenior); // 시니어 체크 여부
  const [pastPax, setPastPax] = useState(initialPastPax);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [pastPaxDraft, setPastPaxDraft] = useState(initialPastPax);
  const [membershipPassengerInfo, setMembershipPassengerInfo] = useState(() => normalizeMembershipPassengerInfo(savedMembershipPassengerInfo));
  const [lookupForm, setLookupForm] = useState({
    firstname: '',
    lastname: '',
    dob: '',
  });
  const [lookupError, setLookupError] = useState('');
  const [lookupSuccess, setLookupSuccess] = useState('');
  const [isLookupLoading, setIsLookupLoading] = useState(false);

  // 3) Nav(on/off) & 탭
  const [showNav,   setShowNav]   = useState(stage === 1);
  const [activeKey, setActiveKey] = useState('tab-1');
  const navContentRef = useRef(null);

  // 4) 검증/로딩
  const [invalidFields, setInvalidFields] = useState({});
  const [isPriceLoading, setIsPriceLoading] = useState(false);

  // 5) 카테고리(등급) & 탭 가격
  const [roomCategories, setRoomCategories] = useState([]);
  const [dynamicTabPrices, setDynamicTabPrices] = useState({});
  const [seniorFlag, setSeniorFlag] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState('');
  const [availabilityMessage, setAvailabilityMessage] = useState(DEFAULT_NO_ROOM_MESSAGE);

// 현재 선박이 예외 규칙(MSC/NCL 등)에 해당하는지 판별
  const exceptionType = useMemo(
    () => identifyCruiseException(passengerAgeConfig?.sourceLineId ?? passengerAgeConfig?.lineId),
    [passengerAgeConfig?.sourceLineId, passengerAgeConfig?.lineId]
  );
  const currentLineId = Number(passengerAgeConfig?.sourceLineId ?? passengerAgeConfig?.lineId);
  const supportsMembership = MEMBERSHIP_ENABLED_LINE_IDS.includes(currentLineId);
  const showsMembershipButton = supportsMembership || MEMBERSHIP_VISIBLE_DISABLED_LINE_IDS.includes(currentLineId);
  const isMembershipButtonDisabled = MEMBERSHIP_VISIBLE_DISABLED_LINE_IDS.includes(currentLineId);
  const supportsMembershipLookup = MEMBERSHIP_LOOKUP_ENABLED_LINE_IDS.includes(currentLineId);
  const isMscLine = currentLineId === MSC_LINE_ID;
  const isMscSeniorPax = !isMscLine || (adults > 0 && children === 0);
  // Only Bellissima departures from Busan are excluded. Use IDs, not translated port names.
  const isBellissimaBusanDeparture = isMscLine
    && Number(cruise?.ship_id ?? cruise?.shipId) === 4511
    && Number(cruise?.departure_port_id) === 1903;
  const supportsSenior = !isBellissimaBusanDeparture
    && (SENIOR_ENABLED_LINE_IDS.includes(currentLineId) || isMscLine) && isMscSeniorPax && !!seniorRule;
  const showMscSeniorNotice = currentLineId === MSC_LINE_ID;
  const showNorwegianFinalFareNotice = currentLineId === 17;
  const membershipLookupLineId = supportsMembershipLookup ? (passengerAgeConfig?.sourceLineId ?? passengerAgeConfig?.lineId ?? '') : '';
  const effectivePastPax = supportsMembership ? pastPax : '';
  const effectiveMembershipPassengerInfo = supportsMembership && effectivePastPax
    ? normalizeMembershipPassengerInfo(membershipPassengerInfo)
    : null;
  const effectiveIncludeSenior = supportsSenior ? includeSenior : false;
  const hasSelectorNotice = !showNav && adults > 0 && (supportsSenior || showMscSeniorNotice);
  const membershipPlaceholder = MEMBERSHIP_PLACEHOLDER_BY_LINE_ID[currentLineId] ?? '선사 회원번호 입력';

  const groupsCacheKey = getBookingGroupsCacheKey(scheduleId);
  const skipGroupsCacheKey = scheduleId ? `booking:skipGroupsCache:${scheduleId}` : null;
  // 현재 항차에서 발급한 세션 키를 우선 적용, 없으면 크루즈/전역 순으로 폴백
  const sessionKeyForSchedule = (() => {
    if (typeof sessionStorage === 'undefined') return '';
    const candidates = [];
    if (scheduleId != null) candidates.push(`booking:sessionKey:${scheduleId}`);
    if (cruise?.codeToCruiseId) candidates.push(`booking:sessionKey:${cruise.codeToCruiseId}`);
    candidates.push('bookingSessionKey');
    for (const key of candidates) {
      try {
        const value = sessionStorage.getItem(key);
        if (value) return value;
      } catch (_) { /* noop */ }
    }
    return '';
  })();
  // YYYY-01-01(API 호출용)
  const childDob = useMemo(() => (
    buildChildDob({
      cruiseDepartureDate: cruise?.departureDate,
      childCount: children,
      childAges: childrenAges,
    })
  ), [cruise?.departureDate, children, childrenAges]);
  const adultDob = useMemo(() => {
    if (!effectiveIncludeSenior) return '';
    return buildSeniorDob({
      cruiseDepartureDate: cruise?.departureDate,
      adultCount: adults,
      referenceAge: seniorRule?.minAge ?? 70,
    });
  }, [effectiveIncludeSenior, cruise?.departureDate, adults, seniorRule?.minAge]);

  const clearScheduleBasketArtifacts = useCallback(() => {
    if (typeof sessionStorage === 'undefined') return;
    if (scheduleId == null) return;
    try {
      const removableKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (isBookingBasketCacheKeyForSchedule(key, scheduleId)) removableKeys.push(key);
      }
      removableKeys.forEach(key => {
        try { sessionStorage.removeItem(key); } catch (_) { /* noop */ }
      });
    } catch (_) { /* noop */ }
    try { sessionStorage.removeItem(`booking:basketHold:${scheduleId}`); } catch (_) { /* noop */ }
    try { sessionStorage.removeItem(`booking:basketSkipFetch:${scheduleId}`); } catch (_) { /* noop */ }
    try {
      window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx: scheduleId } }));
    } catch (_) { /* noop */ }
  }, [scheduleId]);
  const clearGroupsCache = useCallback(() => {
    setRoomCategories([]);
    setDynamicTabPrices({});
    setSeniorFlag(false);
    setAvailabilityStatus('');
    setAvailabilityMessage(DEFAULT_NO_ROOM_MESSAGE);
    setActiveKey('tab-1');
    if (typeof sessionStorage === 'undefined') return;
    try {
      if (groupsCacheKey) sessionStorage.removeItem(groupsCacheKey);
    } catch (_) { /* noop */ }
    try {
      if (skipGroupsCacheKey) sessionStorage.removeItem(skipGroupsCacheKey);
    } catch (_) { /* noop */ }
  }, [groupsCacheKey, skipGroupsCacheKey]);
  const syncPaxState = useCallback((nextPastPax, nextMembershipPassengerInfo = membershipPassengerInfo) => {
    const normalizedPastPax = normalizePastPax(nextPastPax);
    const normalizedMembershipPassengerInfo = normalizedPastPax
      ? normalizeMembershipPassengerInfo(nextMembershipPassengerInfo)
      : null;
    onSyncPax?.({
      counts: { adults, children },
      ages: { children: childrenAges },
      includeSenior: effectiveIncludeSenior,
      pastPax: supportsMembership ? normalizedPastPax : '',
      membershipPassengerInfo: supportsMembership ? normalizedMembershipPassengerInfo : null,
    });
  }, [onSyncPax, adults, children, childrenAges, effectiveIncludeSenior, supportsMembership, membershipPassengerInfo]);

  useEffect(() => {
    setChildrenAges(prev => prev.slice(0, children).concat(Array(Math.max(0, children - prev.length)).fill('')));
  }, [children]);

  useEffect(() => {
    if (!savedCounts) return;
    const normalized = normalizeGuestCounts(savedCounts);
    setAdults(normalized.adults);
    setChildren(normalized.children);
  }, [savedCounts?.adults, savedCounts?.children]);

  useEffect(() => {
    if (!savedAges) return;
    const normalized = normalizeGuestAges(savedAges);
    setChildrenAges(normalized.children);
  }, [savedAges?.children]);

  useEffect(() => {
    setIncludeSenior(supportsSenior ? !!savedIncludeSenior : false);
  }, [savedIncludeSenior, supportsSenior]);

  useEffect(() => {
    const normalizedPastPax = normalizePastPax(savedPastPax);
    const nextPastPax = supportsMembership ? normalizedPastPax : '';
    setPastPax(nextPastPax);
    setPastPaxDraft(nextPastPax);
    setMembershipPassengerInfo(nextPastPax ? normalizeMembershipPassengerInfo(savedMembershipPassengerInfo) : null);
  }, [savedPastPax, savedMembershipPassengerInfo, supportsMembership]);

  useEffect(() => {
    if (adults === 0) {
      setIncludeSenior(false);
    }
  }, [adults]);

  useEffect(() => {
    if (!supportsSenior) {
      setIncludeSenior(false);
    }
  }, [supportsSenior]);

  useEffect(() => {
    if (!supportsMembership) {
      setPastPax('');
      setPastPaxDraft('');
      setMembershipPassengerInfo(null);
      setLookupError('');
      setLookupSuccess('');
      setIsMemberModalOpen(false);
    }
  }, [supportsMembership]);

  /** ── 나이 검증(빈 값 제외, 채워진 값만 검사) ── */
  useEffect(() => {
    const next = {};
    const minAgeNum = childMinAge != null ? Number(childMinAge) : null;
    const maxAgeNum = childMaxAge != null ? Number(childMaxAge) : null;
    const hasMin = Number.isFinite(minAgeNum);
    const hasMax = Number.isFinite(maxAgeNum);
    childrenAges.forEach((value, index) => {
      if (value === '') return;
      const ageNum = Number(value);
      const isInvalid =
        !Number.isFinite(ageNum) ||
        (hasMin && ageNum < minAgeNum) ||
        (hasMax && ageNum > maxAgeNum);
      if (isInvalid) {
        next[`children-age-${index}`] = true;
      }
    });
    setInvalidFields(next);
  }, [childrenAges, childMinAge, childMaxAge]);

  /** ── ‘예약 가능 선실 보기’ → 기본 정보 조회 ── */
  useEffect(() => {
    if (stage === 1) {
      setShowNav(true);
    } else {
      setShowNav(false);
      resetAll();
      setActiveKey('tab-1');
    }
  }, [stage]);

  useEffect(() => {
    if (!cruise || !showNav) return;

    const controller = new AbortController();

    const tryUseCachedGroups = () => {
      if (!groupsCacheKey) return false;
      if (skipGroupsCacheKey) {
        const skip = sessionStorage.getItem(skipGroupsCacheKey);
        if (skip === '1') {
          try { sessionStorage.removeItem(skipGroupsCacheKey); } catch (_) { /* noop */ }
          return false;
        }
      }
      try {
        const cacheStr = sessionStorage.getItem(groupsCacheKey);
        if (!cacheStr) return false;
        const cached = JSON.parse(cacheStr);
        const cachedCounts = normalizeGuestCounts(cached?.counts);
        const cachedChildDob = typeof cached?.childDob === 'string' ? cached.childDob : '';
        const cachedAdultDob = typeof cached?.adultDob === 'string' ? cached.adultDob : '';
        const cachedIncludeSenior = Object.prototype.hasOwnProperty.call(cached || {}, 'includeSenior')
          ? !!cached.includeSenior
          : false;
        const cachedPastPax = normalizePastPax(cached?.pastPax);
        const currentChildDob = typeof childDob === 'string' ? childDob : '';
        const currentAdultDob = typeof adultDob === 'string' ? adultDob : '';
        const currentIncludeSenior = !!effectiveIncludeSenior;
        const currentPastPax = normalizePastPax(effectivePastPax);
        const isSameCounts =
          cachedCounts.adults === adults &&
          cachedCounts.children === children;
        const isSameDob =
          cachedChildDob === currentChildDob &&
          cachedAdultDob === currentAdultDob;
        const isSameIncludeSenior = cachedIncludeSenior === currentIncludeSenior;
        const isSamePastPax = cachedPastPax === currentPastPax;
        if (!isSameCounts || !isSameDob || !isSameIncludeSenior || !isSamePastPax) {
          setSeniorFlag(false);
          return false;
        }

        const cachedGroups = sortCategoriesByPriority(regroupDisneyAdventureCabins(cached.groups || [], cruise));
        const hasCachedAvailabilityStatus = Object.prototype.hasOwnProperty.call(cached || {}, 'availabilityStatus');
        if (cachedGroups.length === 0 && !hasCachedAvailabilityStatus) {
          return false;
        }
        setSeniorFlag(Boolean(cached.seniorFlag));
        setRoomCategories(cachedGroups);
        setDynamicTabPrices(isDisneyAdventure(cruise)
          ? Object.fromEntries(cachedGroups.map(cat => [cat.name, cat.minPrice]))
          : (cached.dynamicTabPrices || {}));
        if (isDisneyAdventure(cruise)) setActiveKey('tab-1');
        setAvailabilityStatus(typeof cached.availabilityStatus === 'string' ? cached.availabilityStatus : '');
        setAvailabilityMessage(
          typeof cached.availabilityMessage === 'string' && cached.availabilityMessage
            ? cached.availabilityMessage
            : DEFAULT_NO_ROOM_MESSAGE
        );
        setIsPriceLoading(false);
        return true;
      } catch (err) {
        console.error('failed to restore cached room groups', err);
        return false;
      }
    };

    if (tryUseCachedGroups()) {
      return () => controller.abort();
    }

    const fetchGroups = async () => {
      setIsPriceLoading(true);
      try {
        const totalChildren = children;
        const url = buildBookingBasicInfoUrl({
          cruiseId: cruise.codeToCruiseId,
          adults,
          children: totalChildren,
          childDob: totalChildren > 0 ? childDob : '',
          includeSenior: effectiveIncludeSenior,
          adultDob: effectiveIncludeSenior ? adultDob : '',
          pastpax: effectivePastPax,
        });
        const res = await apiGet(url, {}, { signal: controller.signal });
        if (controller.signal.aborted) return;

        let shouldClearBasket = false;
        const newSessionKey = res.result?.sessionKey ? String(res.result.sessionKey) : '';

        if (newSessionKey) {
          const issuedAt = Date.now();
          const issuedAtStr = String(issuedAt);
          const scopedKey = scheduleId != null ? String(scheduleId) : (cruise?.codeToCruiseId ?? 'unknown');
          try {
            let prevSessionKey = '';
            try {
              prevSessionKey = sessionStorage.getItem(`booking:sessionKey:${scopedKey}`) || '';
            } catch (_) { /* noop */ }
            if (scheduleId != null && prevSessionKey && prevSessionKey !== newSessionKey) {
              shouldClearBasket = true;
            }

            if (shouldClearBasket) {
              clearScheduleBasketArtifacts();
            }

            sessionStorage.setItem(`booking:sessionKey:${scopedKey}`, newSessionKey);
            sessionStorage.setItem(`booking:sessionKeyIssuedAt:${scopedKey}`, issuedAtStr);
            if (scheduleId != null) {
              sessionStorage.setItem(`booking:sessionKeyIssuedAt:schedule:${scheduleId}`, issuedAtStr);
              if (cruise?.codeToCruiseId) {
                // 항차 idx와 cruiseId의 연결을 기억해 정리 시 두 키 모두 제거 가능하게
                sessionStorage.setItem(`booking:scheduleToCruiseId:${scheduleId}`, String(cruise.codeToCruiseId));
              }
            }
            if (cruise?.codeToCruiseId) {
              sessionStorage.setItem(`booking:sessionKeyIssuedAt:${cruise.codeToCruiseId}`, issuedAtStr);
            }
          } catch (_) { /* noop */ }
        }

        const groups = sortCategoriesByPriority(regroupDisneyAdventureCabins(res.result?.groups || [], cruise));
        const nextAvailabilityStatus = typeof res.result?.availabilityStatus === 'string'
          ? res.result.availabilityStatus
          : '';
        const nextAvailabilityMessage = typeof res.result?.availabilityMessage === 'string' && res.result.availabilityMessage
          ? res.result.availabilityMessage
          : DEFAULT_NO_ROOM_MESSAGE;
        setSeniorFlag(Boolean(res.result?.seniorFlag));
        setRoomCategories(groups);
        if (isDisneyAdventure(cruise)) setActiveKey('tab-1');
        setAvailabilityStatus(nextAvailabilityStatus);
        setAvailabilityMessage(nextAvailabilityMessage);

        const initPrices = {};
        groups.forEach(cat => {
          initPrices[cat.name] = getSeniorAwareCategoryMinPrice(cat, effectiveIncludeSenior) ?? cat.minPrice;
        });
        setDynamicTabPrices(initPrices);

        if (groupsCacheKey) {
          try {
            sessionStorage.setItem(groupsCacheKey, JSON.stringify({
              groups,
              dynamicTabPrices: initPrices,
              counts: { adults, children },
              childDob: typeof childDob === 'string' ? childDob : '',
              adultDob: typeof adultDob === 'string' ? adultDob : '',
              includeSenior: !!effectiveIncludeSenior,
              pastPax: normalizePastPax(effectivePastPax),
              seniorFlag: Boolean(res.result?.seniorFlag),
              availabilityStatus: nextAvailabilityStatus,
              availabilityMessage: nextAvailabilityMessage,
              ts: Date.now(),
            }));
          } catch (_) { /* noop */ }
        }
        if (skipGroupsCacheKey) {
          try { sessionStorage.removeItem(skipGroupsCacheKey); } catch (_) { /* noop */ }
        }
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.error(e);
          setRoomCategories([]);
          setAvailabilityStatus('');
          setAvailabilityMessage(DEFAULT_NO_ROOM_MESSAGE);
        }
      } finally {
        setIsPriceLoading(false);
      }
    };

    fetchGroups();
    return () => controller.abort();
  }, [cruise, showNav, adults, children, groupsCacheKey, skipGroupsCacheKey, childDob, adultDob, effectiveIncludeSenior, effectivePastPax, scheduleId, clearScheduleBasketArtifacts]);

/** deck_info는 “선택하기” 클릭 시점에 호출 */

  /** ── 탭/스크롤 등 ── */
  const tabIndex       = Math.max(0, parseInt(activeKey.replace('tab-',''), 10) - 1);
  const activeCategory = roomCategories[tabIndex];

  const totalCount = adults + children;
  const allChildrenFilled = children === 0 || childrenAges.every(v => v !== '');
  const allFilled = totalCount > 0 && allChildrenFilled;

  function resetAll() {
    setRoomCategories([]);
    setDynamicTabPrices({});
    setSeniorFlag(false);
    setAvailabilityStatus('');
    setAvailabilityMessage(DEFAULT_NO_ROOM_MESSAGE);
  }

  const openMemberModal = useCallback(() => {
    if (!supportsMembership) return;
    setPastPaxDraft(pastPax);
    setLookupError('');
    setLookupSuccess('');
    setIsMemberModalOpen(true);
  }, [pastPax, supportsMembership]);

  const closeMemberModal = useCallback(() => {
    setLookupError('');
    setLookupSuccess('');
    setIsMemberModalOpen(false);
  }, []);

  const handleLookupFieldChange = useCallback((key, value) => {
    setLookupForm(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleLookupPastPax = useCallback(async () => {
    const firstName = lookupForm.firstname.trim();
    const lastName = lookupForm.lastname.trim();
    const dob = lookupForm.dob.trim();
    const lineId = String(membershipLookupLineId || '').trim();

    if (!lineId || !firstName || !lastName || !dob) {
      setLookupSuccess('');
      setLookupError('영문 이름, 영문 성, 생년월일을 모두 입력해 주세요.');
      return;
    }

    setIsLookupLoading(true);
    setLookupError('');
    setLookupSuccess('');

    try {
      const response = await apiGet(buildCruisePaxDataUrl({
        lineId,
        firstName,
        lastName,
        dob,
        cruiseId: cruise?.codeToCruiseId,
        shipId: cruise?.ship_id || cruise?.shipId,
      }));
      const results = Array.isArray(response?.results) ? response.results : [];
      const matched = results.find(item => normalizePastPax(item?.pastpassengerno));
      const resolvedPastPax = normalizePastPax(matched?.pastpassengerno);

      if (!resolvedPastPax) {
        setLookupError('일치하는 회원번호를 찾지 못했습니다.');
        return;
      }

      const nextMembershipPassengerInfo = normalizeMembershipPassengerInfo({
        ...(matched || {}),
        firstName: firstNonEmpty(matched?.firstname, matched?.firstName, firstName),
        lastName: firstNonEmpty(matched?.lastname, matched?.lastName, lastName),
        birthDate: firstNonEmpty(matched?.dob, matched?.birthDate, dob),
      });
      setPastPaxDraft(resolvedPastPax);
      setMembershipPassengerInfo(nextMembershipPassengerInfo);
      setLookupSuccess(`회원번호 ${resolvedPastPax}를 찾았습니다.`);
    } catch (error) {
      console.error('failed to lookup cruise pax data', error);
      setLookupError('회원번호 조회 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsLookupLoading(false);
    }
  }, [lookupForm, membershipLookupLineId, cruise?.codeToCruiseId, cruise?.ship_id, cruise?.shipId]);

  const applyPastPax = useCallback(() => {
    const nextPastPax = normalizePastPax(pastPaxDraft);
    const didChange = nextPastPax !== pastPax;
    const nextMembershipPassengerInfo = nextPastPax ? normalizeMembershipPassengerInfo(membershipPassengerInfo) : null;

    setPastPax(nextPastPax);
    setPastPaxDraft(nextPastPax);
    setMembershipPassengerInfo(nextMembershipPassengerInfo);

    if (didChange) {
      clearScheduleBasketArtifacts();
      clearGroupsCache();
    }
    if (showNav) {
      syncPaxState(nextPastPax, nextMembershipPassengerInfo);
    }

    closeMemberModal();
  }, [pastPaxDraft, pastPax, membershipPassengerInfo, clearScheduleBasketArtifacts, clearGroupsCache, showNav, syncPaxState, closeMemberModal]);

  const clearPastPax = useCallback(() => {
    if (!pastPax) {
      setPastPaxDraft('');
      setMembershipPassengerInfo(null);
      return;
    }

    setPastPax('');
    setPastPaxDraft('');
    setMembershipPassengerInfo(null);
    setLookupError('');
    setLookupSuccess('');
    clearScheduleBasketArtifacts();
    clearGroupsCache();
    if (showNav) {
      syncPaxState('', null);
    }
  }, [pastPax, clearScheduleBasketArtifacts, clearGroupsCache, showNav, syncPaxState]);

function handleToggleNav() {
  if (stage === 0) {
    if (isPriceLoading) return;
    const hasInvalid = Object.keys(invalidFields).length > 0;
    if (totalCount === 0 || !allFilled || hasInvalid) return;
      const payload = {
        counts: { adults, children },
        ages: {
          children: childrenAges,
        },
        includeSenior: effectiveIncludeSenior,
        pastPax: effectivePastPax,
        membershipPassengerInfo: effectiveMembershipPassengerInfo,
      };
      onSubmitPax?.(payload);
    } else {
      resetAll();
      setShowNav(false);
      setActiveKey('tab-1');
      setIncludeSenior(supportsSenior ? !!savedIncludeSenior : false);
      onResetPax?.();
    }
  }

  const handleSeniorCheckboxChange = useCallback(
    (event) => {
      const nextChecked = event.target.checked;
      setIncludeSenior(nextChecked);
    },
    []
  );

  const handleTabSelect = key => {
    setActiveKey(key);
    if (navContentRef.current) {
      const rect = navContentRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset + rect.top - 62;
      window.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  };

  /** “선택하기” 눌렀을 때 최종 payload 전달 */
  const handleChoose = (roomWithFreshDeck) => {
    const payload = {
      carbinGradeInfo: roomWithFreshDeck, // 최신 deck_info 포함
      counts: { adults, children },
      ages:   { children: childrenAges },
      includeSenior: effectiveIncludeSenior,
      pastPax: effectivePastPax,
      membershipPassengerInfo: effectiveMembershipPassengerInfo,
    };
    onNextStep(payload);
  };

  return (
    <>
      <div className="guest-selector">
        <div className="guest-selector-header">
          <div className="guest-selector-note">1개 선실당 투숙 인원</div>
          {showsMembershipButton && (
            <button
              type="button"
              className="guest-membership-button"
              onClick={openMemberModal}
              disabled={isPriceLoading || isMembershipButtonDisabled}
            >
              {pastPax ? '회원번호 변경' : '회원번호 적용'}
            </button>
          )}
        </div>
        <div className="guest-row-wrapper">
          <GuestRow label={passengerAgeConfig.adult.label}   count={adults}   setCount={setAdults}   showNav={showNav} />
          <GuestRow label={passengerAgeConfig.child.label}   count={children} setCount={setChildren} showNav={showNav} />
        </div>

        {!showNav && (
          <>
            <div className="guest-age-wrapper">
              {children > 0 && (
                <>
                  <AgeGroup
                    groupKey="children"
                    label={passengerAgeConfig.child.summaryLabel}
                    min={childMinAge}
                    max={childMaxAge}
                    ages={childrenAges}
                    setAges={setChildrenAges}
                    invalidFields={invalidFields}
                  />
                  <span className='guest-selector-caution'>*승선일 기준 만 나이</span>
                </>
              )}
            </div>
            {Object.keys(invalidFields).length > 0 && (
              <div className="age-error-message">나이를 다시 입력해 주세요.</div>
            )}
            {/* 시니어 체크박스 */}
            {supportsSenior && adults > 0 && (
              <div className="senior-check-wrapper">
                <label className="senior-checkbox">
                  <input
                    type="checkbox"
                    name='senior-chk-box'
                    checked={includeSenior}
                    onChange={handleSeniorCheckboxChange}
                  />
                  <span className="senior-check-description">{seniorRule.description}</span>
                </label>
              </div>
            )}
            {showMscSeniorNotice && adults > 0 && (
              <div className="senior-check-wrapper senior-check-notice">
                <p>{MSC_VOYAGERS_CLUB_NOTICE}</p>
              </div>
            )}
          </>
        )}

        {supportsMembership && pastPax && (
          <div className="guest-membership-status">
            <span>적용된 회원번호</span>
            <strong>{pastPax}</strong>
            <button
              type="button"
              className="guest-membership-clear"
              onClick={clearPastPax}
              disabled={isPriceLoading}
            >
              삭제
            </button>
          </div>
        )}

        {isPriceLoading && (
          <div className="loading-wrapper">
            <Loading isLoading={true} />
          </div>
        )}

        <button
          type="button"
          className="btn-reservation"
          onClick={handleToggleNav}
          style={{ marginTop: hasSelectorNotice ? 6 : 12 }}
          disabled={ isPriceLoading || (stage === 0 && ( !allFilled || Object.keys(invalidFields).length > 0 )) }
        >
          {showNav ? <><Reset /> 탑승 인원 재설정 하기</> : '예약 가능 선실 보기'}
        </button>
      </div>

      {!isPriceLoading && showNav && (
        <div className="nav-content-wrapper" ref={navContentRef}>
          {roomCategories && roomCategories.length > 0 ? (
            <>
              {showNorwegianFinalFareNotice && (
                <p className="norwegian-final-fare-notice">
                  ※ 할인 및 세금을 포함한 최종 요금은 선실 선택 후 확인하실 수 있습니다.
                </p>
              )}

              <Nav
                justify
                variant="tabs"
                activeKey={activeKey}
                onSelect={handleTabSelect}
                className="room-selector"
              >
                {roomCategories.map((cat, i) => (
                  <Nav.Item key={cat.name}>
                    <Nav.Link eventKey={`tab-${i+1}`}>
                      <span>{cat.name}</span><br/>
                      <span>
                        {Number(cat.minPrice ?? dynamicTabPrices[cat.name]).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}~
                      </span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>

              <TabContent
                category={activeCategory}
                onChoose={handleChoose}
                onRatePick={(catName, price)=> setDynamicTabPrices(p=>({ ...p, [catName]: price }))}
                sessionKey={sessionKeyForSchedule}
                cruiseId={cruise?.codeToCruiseId}
                pax={{ adults, children }}
                exceptionType={exceptionType}
                seniorFlag={seniorFlag}
                seniorActive={effectiveIncludeSenior}
              />
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              paddingTop: '20px',
              fontWeight: '400',
              fontSize: '28px',
              lineHeight: '140%',
            }}>
              {availabilityStatus === 'inactive'
                ? (availabilityMessage || INACTIVE_CRUISE_MESSAGE)
                : DEFAULT_NO_ROOM_MESSAGE}
            </div>
          )}
        </div>
      )}

      {supportsMembership && (
        <Modal
          show={isMemberModalOpen}
          onHide={closeMemberModal}
          centered
          className="guest-membership-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>회원번호 적용</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="guest-membership-modal-copy">
              대표 승객 기준 회원번호를 적용하면 해당 요금으로 다시 조회합니다.
            </div>

            {!supportsMembershipLookup ? (
              <div className="guest-membership-panel">
                <label htmlFor="guest-membership-number">회원번호</label>
                <input
                  id="guest-membership-number"
                  type="text"
                  inputMode="numeric"
                  value={pastPaxDraft}
                  onChange={(event) => setPastPaxDraft(event.target.value.trim())}
                  placeholder={membershipPlaceholder}
                />
              </div>
            ) : (
              <div className="guest-membership-panel">
                <div className="guest-membership-fields">
                  <div className="guest-membership-field">
                    <label htmlFor="guest-membership-firstname">영문 이름</label>
                    <input
                      id="guest-membership-firstname"
                      type="text"
                      value={lookupForm.firstname}
                      onChange={(event) => handleLookupFieldChange('firstname', event.target.value.toUpperCase())}
                      placeholder="GILDONG"
                    />
                  </div>
                  <div className="guest-membership-field">
                    <label htmlFor="guest-membership-lastname">영문 성</label>
                    <input
                      id="guest-membership-lastname"
                      type="text"
                      value={lookupForm.lastname}
                      onChange={(event) => handleLookupFieldChange('lastname', event.target.value.toUpperCase())}
                      placeholder="HONG"
                    />
                  </div>
                  <div className="guest-membership-field">
                    <label htmlFor="guest-membership-dob">생년월일</label>
                    <input
                      id="guest-membership-dob"
                      type="date"
                      value={lookupForm.dob}
                      onChange={(event) => handleLookupFieldChange('dob', event.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="guest-membership-lookup"
                  onClick={handleLookupPastPax}
                  disabled={isLookupLoading}
                >
                  {isLookupLoading ? '조회 중...' : '회원번호 조회'}
                </button>

                {lookupError && <p className="guest-membership-feedback is-error">{lookupError}</p>}
                {lookupSuccess && <p className="guest-membership-feedback is-success">{lookupSuccess}</p>}

                <p>영문 이름과 생년월일로 회원번호를 조회할 수 있습니다.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="guest-membership-footer-button is-secondary" onClick={closeMemberModal}>
              취소
            </button>
            <button type="button" className="guest-membership-footer-button is-primary" onClick={applyPastPax}>
              적용하기
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

/** 입력 그룹 */
function AgeGroup({ groupKey, label, ages, setAges, min, max, invalidFields }) {
  return (
    <div className="age-group" key={groupKey}>
      {ages.map((age, i) => {
        const fieldId = `${groupKey}-age-${i}`;
        return (
          <div key={fieldId} className="age-row">
            <label htmlFor={fieldId} className="age-row-contents">
              {label} {i + 1}<span className="require"></span>
            </label>
            <input
              id={fieldId}
              name={fieldId}
              type="number"
              className="age-row-input"
              min={min}
              {...(groupKey !== 'adults' ? { max } : {})}
              value={age}
              placeholder="나이 입력"
              onChange={e => {
                const v = e.target.value;
                setAges(prev => prev.map((x, j) => j === i ? v : x));
              }}
              style={{ borderColor: invalidFields[fieldId] ? 'red' : undefined }}
            />
          </div>
        );
      })}
    </div>
  );
}
